import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, ListChecks, Users, Film, Cog, FileCheck, Save, TrendingUp } from 'lucide-react';
import PublicNav from '../components/PublicNav';
import PublicFooter from '../components/PublicFooter';
import usePageMeta from '../utils/usePageMeta';

const steps = [
  { icon: FileText, title: 'Create a match report', text: 'Start a new analysis for a completed match.' },
  { icon: FileText, title: 'Add match context', text: 'Opponent, score, competition, formation, and tactical focus.' },
  { icon: ListChecks, title: 'Enter key moments and observations', text: 'Log the moments that mattered and what your staff saw.' },
  { icon: Users, title: 'Add player or role review', text: 'Flag selection, fitness, or role concerns if relevant.' },
  { icon: Film, title: 'Attach footage for reference', text: 'Optional: footage is attached to the record, not analyzed automatically.' },
  { icon: Cog, title: 'Tactmark scores key moments and tactical themes', text: 'Algorithmic scoring and tactical weighting run on the structured input.' },
  { icon: TrendingUp, title: 'Tactmark detects recurring patterns', text: 'Compared against your team\u2019s previous reports.' },
  { icon: FileCheck, title: 'Generate the post-match report', text: 'Structured findings are turned into a clear coaching report.' },
  { icon: Save, title: 'Save the report to your workspace', text: 'Every report stays organized by team, season, and opponent.' },
  { icon: TrendingUp, title: 'Track recurring patterns across the season', text: 'The dashboard surfaces issues before they become habits.' }
];

const federationSteps = [
  'Define development objectives',
  'Create programs and assign participating teams',
  'Configure reporting periods and templates',
  'Collect and review standardized program submissions',
  'Assign organizational actions and responsibilities',
  'Review execution and unresolved priorities',
  'Produce technical-leadership and development reports',
];

const sample = {
  title: 'Riverside FC vs Hartley United',
  score: '1 to 2 (Loss)',
  focus: 'Defensive transitions',
  findings: 'The Tactmark Analysis Engine identified the highest-impact moments, recurring tactical themes, and coaching decision risks from the match record.',
  verdict: 'Riverside lost control of the match in a 12-minute window after conceding the equalizer, when three consecutive defensive transitions were mishandled.',
  decisive: [
    "71' Right back stepped high without cover, conceding the counter-attack goal.",
    "58' Missed a clear chance after a well-worked build-up sequence.",
    "83' Failed to manage the game after going ahead in the second half."
  ],
  mistakes: [
    'Defensive line pushed high without a covering midfielder during transitions.',
    'Central midfield spacing broke down after losing the ball in the final third.'
  ],
  coachReview: [
    'The advancing full-back played 78 minutes with declining pressing output. A substitution review is recommended around the 65th minute in similar game states.'
  ],
  pattern: 'This is the fourth match this season where a defensive transition problem has appeared following a change in formation. This is now a recurring pattern, not an isolated event.',
  recommendation: 'Prioritize transition-moment rehearsal this week, with specific attention to full-back recovery positioning.',
  training: 'Defensive transition shape and full-back recovery runs.'
};

export default function HowItWorks() {
  usePageMeta(
    'How Tactmark Works | Post-Match and Federation Workflows',
    'Explore two distinct Tactmark workflows: team-level completed-match review with Post-Match and organization-level technical-development oversight with Federation.'
  );
  return (
    <div className="min-h-screen bg-white">
      <PublicNav />

      <div className="max-w-3xl mx-auto px-6 pt-16 pb-4 text-center">
        <p className="text-navy-600 text-xs font-semibold tracking-widest uppercase mb-4">How it works</p>
        <h1 className="font-serif text-4xl sm:text-5xl text-navy-900 leading-tight mb-6">
          How Tactmark works
        </h1>
        <p className="text-gray-600 text-lg leading-relaxed">
          Tactmark provides two distinct workflows: one for coaching teams reviewing completed matches and one for technical leadership overseeing football development across programs.
        </p>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-16">
        <h2 className="font-serif text-3xl text-navy-900 mb-4">How Tactmark Post-Match works</h2>
        <p className="text-gray-600 leading-relaxed mb-10">Staff submit match context and evidence, confirm the findings that matter, decide what should change, assign a training response, and review the outcome in later matches.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-10">
          {steps.map(({ icon: Icon, title, text }, i) => (
            <div key={title} className="flex gap-4">
              <div className="flex-shrink-0 w-9 h-9 rounded-full bg-navy-50 border border-navy-100 flex items-center justify-center">
                <Icon className="w-4 h-4 text-navy-700" />
              </div>
              <div>
                <p className="text-xs text-navy-400 font-semibold mb-1">STEP {i + 1}</p>
                <h3 className="text-navy-900 font-semibold mb-1">{title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Sample report */}
      <div className="bg-navy-50 border-y border-navy-100">
        <div className="max-w-3xl mx-auto px-6 py-16">
          <p className="text-navy-600 text-xs font-semibold tracking-widest uppercase mb-3">Sample report</p>
          <h2 className="font-serif text-2xl sm:text-3xl text-navy-900 mb-2">{sample.title}</h2>
          <p className="text-gray-500 text-sm mb-8">{sample.score} · Tactical focus: {sample.focus} · Illustrative sample</p>

          <div className="bg-white rounded-2xl border border-gray-200 divide-y divide-gray-100">
            <div className="p-6">
              <p className="text-navy-600 text-xs font-semibold uppercase tracking-wide mb-2">Analysis Engine Findings</p>
              <p className="text-gray-700 text-sm leading-relaxed">{sample.findings}</p>
            </div>
            <div className="p-6">
              <p className="text-navy-600 text-xs font-semibold uppercase tracking-wide mb-2">Verdict summary</p>
              <p className="text-gray-700 text-sm leading-relaxed">{sample.verdict}</p>
            </div>
            <div className="p-6">
              <p className="text-navy-600 text-xs font-semibold uppercase tracking-wide mb-2">Decisive moments</p>
              <ul className="space-y-1.5">
                {sample.decisive.map((d) => <li key={d} className="text-gray-700 text-sm">• {d}</li>)}
              </ul>
            </div>
            <div className="p-6">
              <p className="text-navy-600 text-xs font-semibold uppercase tracking-wide mb-2">Mistake attribution</p>
              <ul className="space-y-1.5">
                {sample.mistakes.map((d) => <li key={d} className="text-gray-700 text-sm">• {d}</li>)}
              </ul>
            </div>
            <div className="p-6">
              <p className="text-navy-600 text-xs font-semibold uppercase tracking-wide mb-2">Coaching Decision Review</p>
              <ul className="space-y-1.5">
                {sample.coachReview.map((d) => <li key={d} className="text-gray-700 text-sm">• {d}</li>)}
              </ul>
            </div>
            <div className="p-6">
              <p className="text-navy-600 text-xs font-semibold uppercase tracking-wide mb-2">Season pattern</p>
              <p className="text-gray-700 text-sm leading-relaxed">{sample.pattern}</p>
            </div>
            <div className="p-6">
              <p className="text-navy-600 text-xs font-semibold uppercase tracking-wide mb-2">Recommendation</p>
              <p className="text-gray-700 text-sm leading-relaxed">{sample.recommendation}</p>
            </div>
            <div className="p-6">
              <p className="text-navy-600 text-xs font-semibold uppercase tracking-wide mb-2">Training focus</p>
              <p className="text-navy-900 font-medium text-sm">{sample.training}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-20 text-center">
        <h2 className="font-serif text-3xl text-navy-900 mb-4">See it built from your own match.</h2>
        <Link
          to="/login?mode=signup"
          className="inline-block bg-navy-900 hover:bg-navy-800 text-white font-medium px-8 py-3 rounded transition-colors mt-2"
        >
          Start free trial
        </Link>
      </div>

      <div className="border-t border-gray-200 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6 py-16">
          <h2 className="font-serif text-2xl sm:text-3xl text-navy-900 mb-4">
            How Tactmark Federation works
          </h2>
          <p className="text-gray-600 leading-relaxed mb-6">
            Federation provides an independent organization-level workflow for technical-development oversight.
          </p>
          <ol className="space-y-3 mb-8">
            {federationSteps.map((step, index) => (
              <li key={step} className="flex gap-3 text-gray-700">
                <span className="font-semibold text-navy-700">{index + 1}.</span>
                <span>{step}</span>
              </li>
            ))}
          </ol>
          <Link to="/contact?plan=federation&from=how_it_works" className="inline-block bg-navy-900 hover:bg-navy-800 text-white font-medium px-6 py-3 rounded transition-colors">Book a Federation Demo</Link>
        </div>
      </div>

      <PublicFooter />
    </div>
  );
}
